import type { ReactNode } from "react";
import { LockKeyhole } from "lucide-react";
import { motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";

export function CampaignDraftSection({
  id,
  title,
  titleExtra,
  icon,
  description,
  locked,
  lockMessage,
  actions,
  children,
  testId,
}: {
  id: string;
  title: string;
  titleExtra?: ReactNode;
  icon?: ReactNode;
  description?: string;
  locked: boolean;
  lockMessage?: string;
  actions?: ReactNode;
  children?: ReactNode;
  testId: string;
}): React.JSX.Element {
  const reduceMotion = useReducedMotion() ?? false;
  const titleId = `${id}-title`;
  const titleRow = (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <h2 id={titleId} className="m-0 flex items-center gap-2 text-[0.9375rem] font-semibold">
        {locked ? (
          <LockKeyhole size={14} className="text-muted" aria-hidden="true" />
        ) : icon ? (
          icon
        ) : null}
        {title}
      </h2>
      {locked ? null : titleExtra}
    </div>
  );

  return (
    <section
      id={id}
      aria-labelledby={titleId}
      data-testid={testId}
      data-locked={locked ? "true" : "false"}
      tabIndex={-1}
      className="min-w-0 max-w-full scroll-mt-4 rounded-lg border border-line/70 bg-surface outline-none"
    >
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line/60 px-4 py-3">
        {description && !locked ? (
          <div className="grid min-w-0 gap-0.5">
            {titleRow}
            <p className="m-0 text-[0.75rem] leading-snug text-muted">{description}</p>
          </div>
        ) : (
          titleRow
        )}
        {locked ? null : actions}
      </header>
      {locked ? (
        <p className="m-0 px-4 py-3 text-[0.8125rem] leading-snug text-muted">{lockMessage}</p>
      ) : (
        <motion.div
          className={cn("grid min-w-0 grid-cols-[minmax(0,1fr)] gap-3 px-4 py-3")}
          initial={reduceMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduceMotion ? 0 : 0.18, ease: "easeOut" }}
        >
          {children}
        </motion.div>
      )}
    </section>
  );
}
